import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../lib/site";
import { SCHOOLS } from "../site/siteData";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { FloppyDisk } from "@phosphor-icons/react";
import { AboutExtraFields } from "./AboutExtraFields";
import { toast } from "sonner";

const SchoolFields = ({ s, onChange }) => {
  const name = SCHOOLS.find((x) => x.slug === s.slug)?.name;
  const set = (k, v) => onChange({ ...s, [k]: v });
  return (
    <Card className="p-5 border-zinc-200 space-y-3" data-testid={`settings-school-${s.slug}`}>
      <h3 className="font-display font-bold text-[#0B2A5C]">{name}</h3>
      <div><Label>Short details (shown in About Us)</Label>
        <Input value={s.details} onChange={(e) => set("details", e.target.value)} placeholder="e.g. State Board · Nursery to Class 10" className="mt-1" data-testid={`settings-${s.slug}-details`} /></div>
      <div><Label>Address *</Label>
        <Input required value={s.address} onChange={(e) => set("address", e.target.value)} className="mt-1" data-testid={`settings-${s.slug}-address`} /></div>
      <div><Label>Phone numbers * (separate with commas)</Label>
        <Input required value={s.phonesText} onChange={(e) => set("phonesText", e.target.value)} className="mt-1" data-testid={`settings-${s.slug}-phones`} /></div>
      <div><Label>Email *</Label>
        <Input required type="email" value={s.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid={`settings-${s.slug}-email`} /></div>
    </Card>
  );
};

export const AboutContactManager = () => {
  const { settings, reload } = useSettings();
  const [about, setAbout] = useState("");
  const [extra, setExtra] = useState({ vision: "", mission: [], commitment: "", motto: "", correspondent_message: "", correspondent_quote: "" });
  const [schools, setSchools] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setAbout(settings.about_text || "");
    setExtra({
      vision: settings.vision || "", mission: settings.mission || [], commitment: settings.commitment || "", motto: settings.motto || "",
      correspondent_message: settings.correspondent_message || "", correspondent_quote: settings.correspondent_quote || "",
    });
    setSchools(settings.schools.map((s) => ({ ...s, phonesText: s.phones.join(", ") })));
  }, [settings]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/site-settings", {
        about_text: about,
        ...extra,
        schools: schools.map(({ phonesText, ...s }) => ({ ...s, phones: phonesText.split(",").map((p) => p.trim()).filter(Boolean) })),
      });
      toast.success("Saved — live on the website");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  if (!settings) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-6">
      <Card className="p-5 border-zinc-200">
        <Label>About Us text *</Label>
        <Textarea required rows={10} value={about} onChange={(e) => setAbout(e.target.value)} className="mt-1" data-testid="settings-about-text" />
        <p className="text-xs text-zinc-500 mt-1">Leave an empty line between paragraphs. The highlight chips above it come from the Achievements tab.</p>
      </Card>
      <AboutExtraFields value={extra} onChange={setExtra} />
      <div className="grid lg:grid-cols-2 gap-6">
        {schools.map((s, i) => (
          <SchoolFields key={s.slug} s={s} onChange={(ns) => setSchools(schools.map((x, j) => (j === i ? ns : x)))} />
        ))}
      </div>
      <Button type="submit" disabled={busy} data-testid="settings-save-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white">
        <FloppyDisk size={16} className="mr-1" />{busy ? "Saving…" : "Save changes"}
      </Button>
    </form>
  );
};
